import { useProjectId } from '@config/config.hooks';
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import {
  Column,
  ComponentType,
  ContentLoader,
  FilesDisplay,
  FormTextField,
  GetClassNameByItemFn,
  Icon,
  LoadingButton,
  TableCellExtras,
  TableRowExtras,
  ToastMessage,
  Tooltip,
  UseFormReturn,
  appendClassNameByCondition,
  createTableCell,
  createTableColumn,
  toastMessage,
} from '@gcl/react-core';
import { v4 as uuidV4 } from 'uuid';

import {
  ETransmittalStatus,
  ETransmittalType,
  TRANSMITTAL_ITEM_ACCEPTED_FILES,
} from '@transmittal/transmittal.constants';
import { useGetTransmittalItemsAsync } from '@transmittal/transmittalItem/transmittalItem.queries';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FETCH_QUERIES_NAME, TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH } from '@common/constants';
import { convertObjectToFileItem } from '@common/fileItems.services';
import { createTransmittalItemDownloadColumn } from '@transmittal/history/tables/transmittalItem/transmittalItemTableColumn.utils';
import { getFilenameAndExt, isMatchPattern } from '@core/Primitives/text.services';
import { useQueryClient } from '@tanstack/react-query';
import { getMasterScheduleDetail, useNominatedInMasterSchedule } from '@masterSchedule/masterSchedule.hooks';
import { ETransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.constants';
import { useBreakPoints } from '@common/breakpoints.hooks';
import { ETransmittalDetailsFormKeys, ETransmittalItemTypes } from '../transmittalDetailPage.constants';
import { ITransmittalItemRowEditingRef, ITransmittalItemTableEditingRef } from './transmittalItemTableForm.models';

import { TransmittalItemTableRowTransmittalRevisionForApproval } from './TransmittalItemTableRowTransmittalRevisionForApproval';
import {
  getTransmittalGeneralDetail,
  useCheckerTransmittalRevisionsForApproval,
  useFormPrefix,
  useRefetch,
  useSiteTransmittalIssuableRevisionUpdateAll,
  useSubmitFormEnabled,
} from './transmittalItemTableForm.hooks';
import { TransmittalItemTableFormHeaderStateButtons } from './TransmittalItemTableFormHeaderStateButtons';
import { TransmittalItemTableRowStateButtons } from './TransmittalItemTableRowStateButtons';
import { TransmittalItemTableRowInUse } from './TransmittalItemTableRowInUse';
import { TransmittalItemTableRowIssueableSelect } from './TransmittalItemTableRowIssueableSelect';
import { ETransmittalItemTableFormKey } from './transmittalItemTableForm.constants';
import { TransmittalItemTableRowTransmittalRevision } from './TransmittalItemTableRowTransmittalRevision';
import { TransmittalItemFileUpload } from './TransmittalItemFileUpload';
import { ITransmittalDetailsFormState } from '../transmittalDetailPage.models';
import { TransmittalItemTableFormMobile } from './transmittalItemTableFormMobile/TransmittalItemTableFormMobile';

interface ITransmittalItemTableFormProps {
  useFormMethods: UseFormReturn;
  shouldShowRemovedFromUse: boolean;
  transmittalId: number;
  docType: IProjectTransmittalItemDocumentType;
  isAnyActionLoading: boolean;
  setIsAnyActionLoading: (isLoading: boolean) => void;
  setRowTableIsEditing: (index: number, isEditing: boolean) => void;
  setRowTableIsUploading: (index: number, isUploading: boolean) => void;
  setTableIsIceDesignCertified: (index: number, isIceDesignCertified: boolean) => void;
  tableIndex: number;
  isReadOnly?: boolean;
  hasDeletePermission: boolean;
  transmittalItemTableEditingRef: React.MutableRefObject<ITransmittalItemTableEditingRef>;
  isWindowFocus: boolean;
  setDefaultValues: (value: React.SetStateAction<ITransmittalDetailsFormState>) => void;
}

export const TransmittalItemTableForm = ({
  useFormMethods,
  tableIndex,
  hasDeletePermission,
  transmittalId,
  docType,
  isReadOnly,
  shouldShowRemovedFromUse,
  setIsAnyActionLoading,
  setRowTableIsEditing,
  setRowTableIsUploading,
  setTableIsIceDesignCertified,
  isAnyActionLoading,
  transmittalItemTableEditingRef,
  isWindowFocus,
  setDefaultValues,
}: ITransmittalItemTableFormProps) => {
  const { isXs } = useBreakPoints();

  const projectId = useProjectId();
  const { transmittalStatus, transmittalType, defaultFileType, masterScheduleId } =
    getTransmittalGeneralDetail(transmittalId);

  const queryClient = useQueryClient();
  const queryKey = [projectId, masterScheduleId, FETCH_QUERIES_NAME.TRANSMITTAL_ITEMS, transmittalId, docType.id];

  const { tablePrefix } = useFormPrefix(tableIndex);

  const rowEditingRef = React.useRef<ITransmittalItemRowEditingRef>({});
  const [editingRowCount, setEditingRowCount] = React.useState(0);
  const refRowUploading = React.useRef<boolean[]>([]);

  const [filesToUpload, setFilesToUpload] = useState<{ [key: string]: File }>({});

  const [isBlockRefetch, setIsBlockRefetch] = useState<boolean>(false);
  const isTableDirty = useFormMethods.getFieldState(`${tablePrefix}`).isDirty;
  useRefetch(isTableDirty, isBlockRefetch);

  const isAnyRowEditing = React.useMemo(() => {
    return editingRowCount > 0;
  }, [JSON.stringify(editingRowCount)]);

  React.useEffect(() => {
    setRowTableIsEditing(tableIndex, isAnyRowEditing);
  }, [isAnyRowEditing]);

  useEffect(() => {
    transmittalItemTableEditingRef.current = {
      ...transmittalItemTableEditingRef.current,
      [tableIndex]: { queryKey, tableRef: rowEditingRef, setRowUploading },
    };
  }, [queryKey, tableIndex]);

  const setRowUploading = (index: number, isUploading: boolean) => {
    const temp = [...refRowUploading.current];
    temp[index] = isUploading;
    refRowUploading.current = temp;
    const isAnyRowUploading = temp.find((item) => item === true) ?? false;
    setRowTableIsUploading(tableIndex, isAnyRowUploading);
  };

  const formFileDrawingItemsWatch: IFormFileDrawingItem[] = useFormMethods.watch(tablePrefix) ?? [];
  const [formFileDrawingItems, setFormFileDrawingItems] = useState<IFormFileDrawingItem[]>([]);
  const originalFiles: IFormFileDrawingItem[] =
    ((useFormMethods.formState.defaultValues ?? {})[ETransmittalDetailsFormKeys.DOCUMENTS] ?? [])[tableIndex] ?? [];
  const { isSubmitEnabled } = useSubmitFormEnabled(useFormMethods, tableIndex);
  useEffect(() => {
    setFormFileDrawingItems([...formFileDrawingItemsWatch]);
  }, [JSON.stringify(formFileDrawingItemsWatch)]);

  const {
    isAllLatestRevision: isAllLatestRevisionST,
    updateAllToLatestRevision: updateAllToLatestRevisionST,
    mutationUpdateAllToLatestRevision: mutationUpdateAllToLatestRevisionST,
  } = useSiteTransmittalIssuableRevisionUpdateAll(masterScheduleId, transmittalId, docType.id, formFileDrawingItems);

  const {
    isSelectAllTransmittalRevisionsForApproval,
    mutationSelectAllTransmittalRevisionsForApproval,
    onClickSelectAll: onSelectAllTransmittalRevisionsForApproval,
  } = useCheckerTransmittalRevisionsForApproval(masterScheduleId, transmittalId, formFileDrawingItems, docType);

  const { masterSchedule } = getMasterScheduleDetail(projectId, masterScheduleId, false);
  const { isNominatedChecker: isPermittedSendApproval } = useNominatedInMasterSchedule(masterSchedule);
  const {
    data: transmittalItemsWithLatestRevision,
    isLoading: isLoadingTransmittalItems,
    isFetching: isRefetchingTransmittalItems,
  } = useGetTransmittalItemsAsync(
    {
      projectId,
      masterScheduleId,
      transmittalId,
      documentTypeId: docType.id,
    },
    isTableDirty,
  );

  React.useEffect(() => {
    queryClient.invalidateQueries(queryKey);
  }, [transmittalStatus]);

  React.useEffect(() => {
    if (!transmittalItemsWithLatestRevision) return;
    const newVal: IFormFileDrawingItem[] = [];

    if (transmittalType === ETransmittalType.ICE) {
      const foundCertified = transmittalItemsWithLatestRevision.find(
        (r) => r.transmittalRevision?.isIceDesignCertified,
      );
      setTableIsIceDesignCertified(tableIndex, !!foundCertified);
    }

    for (let i = 0; i < transmittalItemsWithLatestRevision.length; i += 1) {
      const transmittalItemWithLatestRevision = transmittalItemsWithLatestRevision[i];
      if (!transmittalItemWithLatestRevision) {
        newVal.push(transmittalItemWithLatestRevision);
        continue;
      }
      if (
        transmittalType === ETransmittalType.DESIGNER &&
        docType.id !== ETransmittalItemDocumentType.CERT &&
        transmittalItemWithLatestRevision.initiatedProjectTransmittalType !== ETransmittalType.DESIGNER
      )
        continue;

      if (!shouldShowRemovedFromUse) {
        if (!transmittalItemWithLatestRevision.inUse) continue;
      }

      const fileItem = convertObjectToFileItem(
        transmittalItemWithLatestRevision,
        transmittalItemWithLatestRevision.transmittalRevision
          ? 'transmittalRevision.projectFile.fileName'
          : 'latestRevision.projectFile.fileName',
        transmittalItemWithLatestRevision.transmittalRevision
          ? 'transmittalRevision.projectFile'
          : 'latestRevision.projectFile',
      );

      const uploadedFile = ((useFormMethods.getValues(tablePrefix) ?? []) as IFormFileDrawingItem[]).find(
        (localFile) => {
          return (
            localFile.form?.item?.id === transmittalItemWithLatestRevision.id ||
            localFile.form?.projectFile?.uuid ===
              transmittalItemWithLatestRevision.transmittalRevision?.projectFile?.uuid
          );
        },
      );

      const transmittalItemRevision = transmittalItemWithLatestRevision.transmittalRevision ?? {
        ...transmittalItemWithLatestRevision.latestRevision,
        rowVersion: undefined,
        id: undefined,
      };

      const {
        masterSchedule: masterScheduleLocal,
        transmittal,
        project,
        ...restTransmittalItemRevision
      } = transmittalItemRevision;
      const uuid = transmittalItemWithLatestRevision.transmittalRevision?.projectFile?.uuid ?? uuidV4();
      newVal.push({
        extension: fileItem.extension,
        fileInfo: fileItem.fileInfo,
        file: undefined,
        transmittalItem: transmittalItemWithLatestRevision,
        form: {
          masterScheduleId,
          transmittalId,
          projectId,
          itemId: transmittalItemWithLatestRevision.id,
          item: {
            id: transmittalItemWithLatestRevision.id,
            projectId: transmittalItemWithLatestRevision.projectId,
            masterScheduleId: transmittalItemWithLatestRevision.masterScheduleId,
            docNo: transmittalItemWithLatestRevision.docNo,
            description: transmittalItemWithLatestRevision.description,
            docType: transmittalItemWithLatestRevision.docType,
            inUse: transmittalItemWithLatestRevision.inUse,
            siteInUse: transmittalItemWithLatestRevision.siteInUse,
            isRegistered: transmittalItemWithLatestRevision.isRegistered,
            type: transmittalItemWithLatestRevision.type,
            lastTransmittalId: transmittalItemWithLatestRevision.lastTransmittalId,
            lastSiteTransmittalId: transmittalItemWithLatestRevision.lastSiteTransmittalId,
            rowVersion: transmittalItemWithLatestRevision.rowVersion,
          },
          uuid,
          projectFile:
            uploadedFile?.form?.projectFile?.uploadState === 'finish' ? null : uploadedFile?.form?.projectFile ?? null,

          id: transmittalItemWithLatestRevision.transmittalRevision?.id,

          revisionNo: transmittalItemWithLatestRevision.transmittalRevision?.revisionNo,
          description: transmittalItemWithLatestRevision.description,
          type: transmittalItemWithLatestRevision.transmittalRevision?.type ?? transmittalType,
          designerRevisionId: transmittalItemWithLatestRevision.transmittalRevision?.designerRevisionId,
          isIceDesignCertified: transmittalItemWithLatestRevision.transmittalRevision?.isIceDesignCertified,
          isReviewerDesignCertified: transmittalItemWithLatestRevision.transmittalRevision?.isReviewerDesignCertified,
          lastModifiedDateTime:
            uploadedFile?.transmittalItem?.rowVersion === transmittalItemWithLatestRevision.rowVersion
              ? uploadedFile?.form?.lastModifiedDateTime
              : undefined,
          rowVersion: transmittalItemWithLatestRevision.transmittalRevision?.rowVersion,
        },
      } as IFormFileDrawingItem);
    }
    setDefaultValues((old) => {
      const newSections = old?.[ETransmittalDetailsFormKeys.DOCUMENTS];
      newSections[tableIndex] = newVal;
      return {
        ...old,
        [ETransmittalDetailsFormKeys.DOCUMENTS]: newSections,
      };
    });
  }, [JSON.stringify(transmittalItemsWithLatestRevision), shouldShowRemovedFromUse]);

  React.useEffect(() => {
    const oldFormValues: IFormFileDrawingItem[] = useFormMethods.getValues(tablePrefix) ?? [];
    const originalFilesLocal: IFormFileDrawingItem[] =
      originalFiles.length <= 0
        ? useFormMethods.formState.defaultValues?.[ETransmittalDetailsFormKeys.DOCUMENTS]?.[tableIndex] ?? []
        : originalFiles;
    if (oldFormValues.length <= 0) {
      useFormMethods.setValue(tablePrefix, originalFilesLocal);
      return;
    }

    if (
      !isXs &&
      editingRowCount <= 0 &&
      oldFormValues.every(
        (s) =>
          s.form?.projectFile?.uploadState !== 'uploadingRevision' &&
          s.form?.projectFile?.uploadState !== 'uploadingItem',
      )
    ) {
      useFormMethods.setValue(tablePrefix, originalFilesLocal);
      return;
    }

    const temp = oldFormValues
      .map((oldFile) => {
        const updatedFile = originalFilesLocal.find(
          (original) =>
            original?.form?.uuid === oldFile?.form?.uuid || original?.form?.item?.id === oldFile?.form?.item?.id,
        );
        if (updatedFile) {
          return {
            ...updatedFile,
            form: {
              ...updatedFile.form,
              uuid: oldFile.form?.uuid,
              lastModifiedDateTime: oldFile.form?.lastModifiedDateTime,
              revisionNo: oldFile.form?.revisionNo,
              item: {
                ...updatedFile.form?.item,
                docNo: oldFile.form?.item?.docNo,
                description: oldFile.form?.item?.description,
              },
            },
          } as IFormFileDrawingItem;
        }
        return oldFile;
      })
      .filter((s) => !(!s.transmittalItem && !s.form?.projectFile));

    useFormMethods.setValue(tablePrefix, temp);
  }, [JSON.stringify(originalFiles)]);

  const isAllowUploadDocument =
    docType.allowUpload &&
    !isReadOnly &&
    transmittalType !== ETransmittalType.ICE &&
    transmittalType !== ETransmittalType.REVIEWER;

  const onAddedFile = useCallback(
    (addedFile: any, isFromDropzone = true, indexForNewRevision: number | undefined = undefined) => {
      if (!addedFile || addedFile === null) return;
      if (isFromDropzone && !addedFile.upload) return;

      const accepted = isFromDropzone ? TRANSMITTAL_ITEM_ACCEPTED_FILES.includes(addedFile.type) : true;

      const { filename, name } = isFromDropzone ? addedFile.upload : addedFile;
      const filenameLocal = isFromDropzone ? filename : name;
      const { fileName: filenameWithoutExt, extension } = getFilenameAndExt(filenameLocal);

      if (!accepted) {
        toastMessage(
          <ToastMessage
            message={`${filename} is not a PDF`}
            toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
          />,
        );
        return;
      }

      const oldLocalFiles: IFormFileDrawingItem[] = useFormMethods.getValues(tablePrefix) ?? [];
      let shouldAddNewFile = true;

      const newUuid = uuidV4();

      const formCommon = {
        projectId,
        transmittalId,
        masterScheduleId,
        uuid: newUuid,
      };

      const newLocalFiles = oldLocalFiles.map((oldLocalFile, index) => {
        const itemFileName =
          oldLocalFile.form?.projectFile?.fileName ??
          oldLocalFile.transmittalItem?.transmittalRevision?.projectFile?.fileName ??
          oldLocalFile.transmittalItem?.latestRevision?.projectFile?.fileName;

        if (
          indexForNewRevision === undefined &&
          isMatchPattern(itemFileName ?? '', filenameLocal) &&
          oldLocalFile.transmittalItem?.initiatedProjectTransmittalType !== transmittalType
        ) {
          shouldAddNewFile = false;

          toastMessage(
            <ToastMessage
              message={`Cannot upload new revision for transmittal item (Doc. No.: ${
                oldLocalFile.form?.item?.docNo
              }, FileName: ${
                oldLocalFile.transmittalItem?.latestRevision?.projectFile?.fileName ??
                oldLocalFile.transmittalItem?.transmittalRevision?.projectFile?.fileName
              }). This item is initiated by other transmittal type.`}
              toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
            />,
          );
          // this case happens when user uploads file with same filename of existing item (init by other transmittal type)
          // via drag & drop or upload file in header
          return oldLocalFile;
        }
        if (indexForNewRevision === undefined && !isMatchPattern(itemFileName ?? '', filenameLocal)) {
          return oldLocalFile;
        }
        if (indexForNewRevision !== undefined && indexForNewRevision !== index) {
          return oldLocalFile;
        }
        if (
          !oldLocalFile.form?.item?.inUse &&
          oldLocalFile.transmittalItem?.lastTransmittalId &&
          oldLocalFile.transmittalItem?.lastTransmittalId !== transmittalId
        ) {
          return oldLocalFile;
          // found not in use item with same filename
          // and this item is from endorsed transmittal
          // -> should add new item
        }

        shouldAddNewFile = false;

        if (!oldLocalFile.form?.item?.inUse) {
          toastMessage(
            <ToastMessage
              message={`Found ${filenameLocal} (Doc. No.: ${oldLocalFile.form?.item?.docNo}) is not in use. Please mark the item in use to upload its new revision.`}
              toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
            />,
          );
          return oldLocalFile;
        }

        setFilesToUpload((old) => {
          return { ...old, [newUuid]: addedFile };
        });
        return {
          // new transmittal item revision
          ...oldLocalFile,
          form: {
            ...oldLocalFile.form,
            ...formCommon,
            projectFile: {
              uuid: newUuid,
              fileName: filenameLocal,
              uploadState: 'uploadRevision',
              fileType: defaultFileType,
            },
          },
        } as IFormFileDrawingItem;
      });

      if (shouldAddNewFile) {
        // new transmittal item
        newLocalFiles.push({
          extension,
          form: {
            ...formCommon,
            projectFile: {
              uuid: newUuid,
              fileName: filenameLocal,
              uploadState: 'uploadItem',
              fileType: defaultFileType,
              projectId,
            },
            item: {
              projectId,
              masterScheduleId,
              docNo: filenameWithoutExt,
              description: null,
              docType: docType.id,
              inUse: true,
              siteInUse: true,
              type: transmittalType,
            },

            revisionNo: null,
            description: undefined,
            type: transmittalType,
          },
        } as IFormFileDrawingItem);
      }

      setFilesToUpload((old) => {
        return { ...old, [newUuid]: addedFile };
      });

      const newDocs = [...useFormMethods.getValues(ETransmittalDetailsFormKeys.DOCUMENTS)] as IFormFileDrawingItem[][];
      newDocs[tableIndex] = newLocalFiles;
      useFormMethods.setValue(ETransmittalDetailsFormKeys.DOCUMENTS, newDocs);
    },
    [defaultFileType],
  );

  const onAddedFiles = useCallback(
    (addedFiles: any[]) => {
      addedFiles.forEach((addedFile) => onAddedFile(addedFile));
    },
    [onAddedFile],
  );

  const columns = React.useMemo(() => {
    let cols = isAllowUploadDocument
      ? [
          {
            Header: ({ cellExtras }: any) => {
              return (
                <TransmittalItemTableFormHeaderStateButtons
                  isSubmitEnabled={cellExtras.isSubmitEnabled}
                  setRowUploading={setRowUploading}
                  tableIndex={tableIndex}
                  transmittalItemTableEditingRef={transmittalItemTableEditingRef}
                  isAnyActionLoading={cellExtras.isAnyActionLoading}
                  setEditingRowCount={setEditingRowCount}
                  editingRowCount={cellExtras.editingRowCount}
                  useFormMethods={useFormMethods}
                  refRowEditingEvents={rowEditingRef}
                  masterScheduleId={masterScheduleId}
                  queryKey={queryKey}
                  transmittalId={transmittalId}
                  transmittalType={transmittalType ?? ETransmittalType.DESIGNER}
                />
              );
            },
            accessor: 'id',
            width: 70,
            disableResizing: true,
            disableSortBy: true,
            Cell: createTableCell<string, any>(
              (
                value: string,
                item: IFormFileDrawingItem,
                index: number,
                cellExtras: { [key: string]: any },
                rowExtras,
              ) => {
                if (item.transmittalItem && item.transmittalItem?.initiatedProjectTransmittalType !== transmittalType)
                  return null;
                const { setRowState, rowState } = rowExtras;
                return (
                  <TransmittalItemTableRowStateButtons
                    isAnyActionLoading={cellExtras.isAnyActionLoading}
                    setRowUploading={setRowUploading}
                    refRowEditingEvents={rowEditingRef}
                    setEditingRowCount={setEditingRowCount}
                    masterScheduleId={masterScheduleId}
                    queryKey={queryKey}
                    hasDeletePermission={
                      (cellExtras.hasDeletePermission ?? false) && !item?.transmittalItem?.latestRevision
                    }
                    useFormMethods={cellExtras.useFormMethods}
                    transmittalId={transmittalId}
                    item={item}
                    tableIndex={tableIndex}
                    rowIndex={index}
                    setRowState={setRowState}
                    rowState={rowState}
                  />
                );
              },
            ),
          } as Column,
        ]
      : [];
    if (
      transmittalType !== ETransmittalType.ICE &&
      transmittalType !== ETransmittalType.REVIEWER &&
      docType.allowUpload
    ) {
      cols.push(
        createTableColumn({
          Header: 'In use',
          accessor: `id2`,
          disableResizing: true,
          disableSortBy: true,
          width: 50,
          Cell: createTableCell<string, any>(
            (
              value: string,
              item: IFormFileDrawingItem,
              index: number,
              cellExtras: { [key: string]: any },
              rowExtras,
            ) => {
              const { setRowState, rowState } = rowExtras;

              return (
                <TransmittalItemTableRowInUse
                  rowIndex={index}
                  useFormMethods={cellExtras.useFormMethods}
                  item={item}
                  transmittalId={transmittalId}
                  transmittalType={transmittalType}
                  transmittalStatus={transmittalStatus}
                  queryKey={queryKey}
                  setRowState={setRowState}
                  rowState={rowState}
                  isAnyActionLoading={cellExtras.isAnyActionLoading}
                  setRowUploading={setRowUploading}
                />
              );
            },
          ),
        } as Column),
      );
    }
    cols = cols.concat([
      createTableColumn({
        Header: 'Doc No.',
        accessor: `form.${ETransmittalItemTableFormKey.docNo}`,
        disableResizing: true,
        disableSortBy: true,
        width: 200,
        Cell: createFormTextFieldCell(
          useFormMethods,
          tablePrefix,
          `${ETransmittalItemTableFormKey.docNo}`,
          (item) => {
            return item.form?.item?.docNo;
          },
          (item: IFormFileDrawingItem, value: string | undefined) =>
            item.form?.projectFile?.uploadState !== 'finish' && !item.transmittalItem ? <></> : <div>{value}</div>,
        ),
      } as Column),
      createTableColumn({
        Header: 'Filename',
        accessor: 'form.fileName',
        disableResizing: true,
        disableSortBy: true,
        width: 200,
        Cell: createTableCell<string, any>(
          (value: string, item: IFormFileDrawingItem, index: number, cellExtras: { [key: string]: any }) => {
            if (
              item.form?.projectFile?.fileName &&
              (item.form.projectFile.uploadState === 'uploadingItem' ||
                item.form?.projectFile?.uploadState === 'finish' ||
                item.form?.projectFile?.uploadState === 'error')
            ) {
              return <div>{item.form?.projectFile?.fileName}</div>;
            }
            return (
              <div>
                {item.transmittalItem?.transmittalRevision?.projectFile?.fileName ??
                  item.transmittalItem?.latestRevision?.projectFile?.fileName}
              </div>
            );
          },
        ),
      } as Column),
      createTableColumn({
        Header: 'Description',
        accessor: `${ETransmittalItemTableFormKey.description}`,
        disableSortBy: true,
        Cell: createFormTextFieldCell(useFormMethods, tablePrefix, ETransmittalItemTableFormKey.description, (item) => {
          return item.form?.item?.description ?? undefined;
        }),
      } as Column),
    ]);
    if (transmittalType !== ETransmittalType.ICE)
      cols.push(
        createTransmittalItemDownloadColumn(
          'fileInfo.currentRevision.revision',
          transmittalType === ETransmittalType.SITE || docType.id === ETransmittalItemDocumentType.CERT
            ? 'Previously Issued Rev.'
            : 'Previously Approved Rev.',
          (value, item: IFormFileDrawingItem) => {
            if (!item?.transmittalItem?.latestRevision) return {};
            return {
              projectFileId: item.transmittalItem.latestRevision.projectFile?.id,
              displayText: item.transmittalItem.latestRevision.revisionNo,
              projectId,
            };
          },
          true,
          'gcl-gems-transmittal-item-download-column',
        ),
      );
    cols.push({
      Header: ({ cellExtras }: any) => {
        if (transmittalType === ETransmittalType.REVIEWER) return <div>For Approval</div>;
        if (transmittalType === ETransmittalType.ICE && transmittalStatus === ETransmittalStatus.DRAFT) {
          const {
            onSelectAllTransmittalRevisionsForApproval: onSelectAllTransmittalRevisionsForApprovalLocal,
            mutationSelectAllTransmittalRevisionsForApproval: mutationSelectAllTransmittalRevisionsForApprovalLocal,
          } = cellExtras;
          const {
            isLoading: isLoadingSelectAllTransmittalRevisionsForApproval,
            isSuccess: isSuccessSelectAllTransmittalRevisionsForApproval,
            isError: isErrorSelectAllTransmittalRevisionsForApproval,
          } = mutationSelectAllTransmittalRevisionsForApprovalLocal;
          if (cellExtras.isSelectAllTransmittalRevisionsForApproval?.isShow)
            return (
              <div>
                Transmittal revision{' '}
                {cellExtras.isSelectAllTransmittalRevisionsForApproval?.isShow && (
                  <LoadingButton
                    timeToResetSuccessState={3000}
                    timeToResetErrorState={3000}
                    id={`ct-checkbox-check-all-${docType.id}`}
                    disabled={
                      cellExtras?.isAnyActionLoading ||
                      cellExtras?.isRefetchingTransmittalItems ||
                      isLoadingSelectAllTransmittalRevisionsForApproval
                    }
                    small
                    type={ComponentType.Primary}
                    onClick={() => {
                      onSelectAllTransmittalRevisionsForApprovalLocal(
                        cellExtras.isSelectAllTransmittalRevisionsForApproval.isSelectAll,
                      );
                    }}
                    isSuccess={isSuccessSelectAllTransmittalRevisionsForApproval}
                    isError={isErrorSelectAllTransmittalRevisionsForApproval}
                    isLoading={isLoadingSelectAllTransmittalRevisionsForApproval}
                  >
                    {cellExtras.isSelectAllTransmittalRevisionsForApproval.isSelectAll ? 'Select All' : 'Unselect All'}
                  </LoadingButton>
                )}
              </div>
            );
        }
        return <div>Transmittal revision</div>;
      },
      accessor: `form.${ETransmittalItemTableFormKey.revisionNo}`,
      disableSortBy: true,
      width: TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH,

      Cell: createTableCell<string, any>(
        (
          tempValue: string,
          item: IFormFileDrawingItem,
          index: number,
          cellExtras: { [key: string]: any },
          rowExtras,
        ) => {
          const { rowState } = rowExtras;

          const onRenderIdle = (rowItem: IFormFileDrawingItem, value: string | undefined) => {
            const inUse = rowItem.form?.item?.inUse ?? true; // ?? rowItem.form?.transmittalItemRevision?.item?.inUse ?? true;
            const attachmentId = rowItem?.transmittalItem?.transmittalRevision?.projectFile?.id;
            const displayText = rowItem?.transmittalItem?.transmittalRevision?.revisionNo;
            return (
              <TransmittalItemTableRowTransmittalRevision
                rowIndex={index}
                tableIndex={tableIndex}
                setRowUploading={cellExtras.setRowUploading}
                queryKey={queryKey}
                itemRevisionId={rowItem?.transmittalItem?.transmittalRevision?.id}
                transmittalId={transmittalId}
                inUse={inUse}
                transmittalType={transmittalType}
                attachmentId={attachmentId}
                downloadFileText={displayText || value}
                hasDeletePermission={
                  (cellExtras.hasDeletePermission ?? false) &&
                  !!item?.transmittalItem?.latestRevision &&
                  docType.allowUpload
                }
                disabledDeleteRevision={item.form?.projectFile?.uploadState === 'uploadingRevision'}
                prevRevision={rowItem.transmittalItem?.latestRevision?.revisionNo}
              />
            );
          };
          if (
            (transmittalType === ETransmittalType.ICE || transmittalType === ETransmittalType.REVIEWER) &&
            docType.allowUpload
          )
            return (
              <TransmittalItemTableRowTransmittalRevisionForApproval
                cellExtras={cellExtras}
                projectId={projectId}
                masterScheduleId={masterScheduleId}
                tempValue={tempValue}
                item={item}
                index={index}
                transmittalId={transmittalId}
                transmittalType={transmittalType}
                transmittalStatus={cellExtras.transmittalStatus}
                setRowUploading={cellExtras.setRowUploading}
                docType={docType}
                onRenderIdle={onRenderIdle}
                isReadOnly={isReadOnly}
                queryKey={queryKey}
              />
            );
          if (rowState === 'Editing' && !item.form?.projectFile) return onRenderIdle(item, tempValue);

          return renderFormTextFieldCell(
            useFormMethods,
            tempValue,
            item,
            index,
            cellExtras,
            rowExtras,
            `${tablePrefix}[${index}].${ETransmittalItemTableFormKey.form}.${ETransmittalItemTableFormKey.revisionNo}`,
            (rowItem) => {
              return rowItem.form?.revisionNo ?? rowItem.transmittalItem?.transmittalRevision?.revisionNo;
            },
            onRenderIdle,
          );
        },
      ),
    } as Column);

    if (isAllowUploadDocument) {
      cols.push(
        createTableColumn({
          Header: ({ cellExtras }: any) => {
            const {
              mutationUpdateAllToLatestRevisionST: mutationUpdateAllToLatestRevisionSTLocal,
              updateAllToLatestRevisionST: updateAllToLatestRevisionSTLocal,
            } = cellExtras;

            const isShowUpdateAllRevisionsBtnLocal = !cellExtras.isAllLatestRevisionST;

            if (!isShowUpdateAllRevisionsBtnLocal) return <></>;

            const {
              isLoading: isLoadingMutationUpdateAllToLatestRevisionST,
              isSuccess: isSuccessMutationUpdateAllToLatestRevisionST,
              isError: isErrorMutationUpdateAllToLatestRevisionST,
            } = mutationUpdateAllToLatestRevisionSTLocal;
            return (
              <div className="gcl-gems-transmittal-item-issuable-select-header">
                <Tooltip overlay="Update all to latest revisions">
                  <LoadingButton
                    id={`st-refresh-all-${docType.id}`}
                    className="gcl-gems-transmittal-document-table-form-update-all-revision-btn"
                    disabled={
                      cellExtras?.isAnyActionLoading ||
                      cellExtras?.isRefetchingTransmittalItems ||
                      isLoadingMutationUpdateAllToLatestRevisionST
                    }
                    onClick={() => {
                      updateAllToLatestRevisionSTLocal();
                    }}
                    type={ComponentType.Primary}
                    isLoading={isLoadingMutationUpdateAllToLatestRevisionST}
                    isSuccess={isSuccessMutationUpdateAllToLatestRevisionST}
                    isError={isErrorMutationUpdateAllToLatestRevisionST}
                    timeToResetErrorState={3000}
                    timeToResetSuccessState={3000}
                  >
                    Update All Revisions
                  </LoadingButton>
                </Tooltip>
              </div>
            );
          },

          accessor: 'fileInfo.type',
          width: 100,
          disableResizing: true,
          disableSortBy: true,
          Cell: createTableCell<ETransmittalItemTypes, IFormFileDrawingItem>(
            (
              value: ETransmittalItemTypes,
              item: IFormFileDrawingItem,
              rowIndex: number,
              cellExtras: { [key: string]: any },
              rowExtras,
            ) => {
              const { setRowState } = rowExtras;

              if (cellExtras.transmittalStatus === ETransmittalStatus.APPROVED) return null;

              if (
                cellExtras.transmittalType === ETransmittalType.SITE &&
                item.transmittalItem?.initiatedProjectTransmittalType === ETransmittalItemTypes.DESIGNER &&
                item.transmittalItem?.id
              )
                return (
                  <TransmittalItemTableRowIssueableSelect
                    item={item}
                    masterScheduleId={masterScheduleId}
                    transmittalId={transmittalId}
                    docType={docType}
                    transmittalType={transmittalType}
                    isAnyActionLoading={cellExtras.isAnyActionLoading}
                    setRowUploading={cellExtras?.setRowUploading}
                    index={rowIndex}
                    isRefetchingTransmittalItems={cellExtras?.isRefetchingTransmittalItems}
                    queryKey={queryKey}
                  />
                );
              if (item.transmittalItem && item.transmittalItem?.initiatedProjectTransmittalType !== transmittalType)
                return null;
              if (!item.form?.item?.inUse) return null;
              return (
                <>
                  <TransmittalItemFileUpload
                    defaultFileType={cellExtras.defaultFileType}
                    setRowState={setRowState}
                    setIsBlockRefetch={setIsBlockRefetch}
                    cellExtras={cellExtras}
                    docType={docType}
                    item={item}
                    rowIndex={rowIndex}
                    tableIndex={tableIndex}
                    useFormMethods={useFormMethods}
                    newTransmittalItemFile={cellExtras.filesToUpload[item.form.uuid]}
                    newTransmittalItemFileUuid={item.form?.uuid}
                    setFilesToUpload={setFilesToUpload}
                  />
                </>
              );
            },
          ),
        } as Column),
      );
    }
    return cols;
  }, [JSON.stringify(transmittalType), transmittalStatus, isAllowUploadDocument, JSON.stringify(queryKey)]);

  const extraHeaderButtons = useMemo(() => {
    if (!isAllowUploadDocument) return [];
    const btns: ReactElement[] = [];
    if (!isXs)
      btns.push(
        <span key={`gcl-gems-transmittal-document-table-${docType.id}-desc`} className="gcl-mr-xs">
          Drag and drop or click upload button to upload file
        </span>,
      );
    btns.push(
      <Tooltip overlay="Upload New File" id="upload-new-file" key="upload-new-file">
        <label
          data-testid={`upload-attachments-${docType.name}`}
          onChange={(e) => {
            const { files } = e.target as HTMLInputElement;
            if ((files ?? []).length <= 0) {
              return;
            }

            for (let index = 0; index < (files ?? []).length; index += 1) {
              if (!files) break;
              const file = files[index];
              onAddedFile(file, false);
            }
          }}
          htmlFor={`gcl-gems-transmittal-document-table-${docType.id}-file-input`}
        >
          {!isAnyActionLoading && (
            <input
              multiple
              name=""
              type="file"
              accept="application/pdf"
              id={`gcl-gems-transmittal-document-table-${docType.id}-file-input`}
              hidden
              onClick={(e) => {
                const element = e.target as HTMLInputElement;
                element.value = '';
              }}
            />
          )}
          <Icon
            iconName="Upload"
            className={appendClassNameByCondition(
              isAnyActionLoading,
              'gcl-files-display-icon-button gcl-ic-btn-lg gcl-fc-primary-400',
              'gcl-cursor-default',
            )}
            disabled={isAnyActionLoading}
          />
        </label>
      </Tooltip>,
    );
    return btns;
  }, [isAllowUploadDocument, isAnyActionLoading, isXs]);

  return (
    <div
      className={`${appendClassNameByCondition(
        isAnyRowEditing,
        'gcl-gems-transmittal-item-table-form-container',
        'gcl-gems-transmittal-item-table-form-container-editing',
      )} ${
        transmittalType === ETransmittalType.REVIEWER ? 'gcl-gems-transmittal-item-table-form-container-reviewer' : ''
      }`}
    >
      <ContentLoader
        active={isLoadingTransmittalItems}
        linePadding={10}
        paragraphConfigs={[{ suffixBlankRow: false, numberOfRows: 5, lineHeight: 10 }]}
      >
        {isXs && (
          <TransmittalItemTableFormMobile
            isAnyActionLoading={isAnyActionLoading}
            isRefetchingTransmittalItems={isRefetchingTransmittalItems}
            isWindowFocus={isWindowFocus}
            filesToUpload={filesToUpload}
            setFilesToUpload={setFilesToUpload}
            setIsBlockRefetch={setIsBlockRefetch}
            hasDeletePermission={hasDeletePermission}
            headerButtons={extraHeaderButtons}
            isAllowUploadDocument={isAllowUploadDocument}
            items={formFileDrawingItems}
            tableIndex={tableIndex}
            useFormMethods={useFormMethods}
            docType={docType}
            transmittalId={transmittalId}
            queryKey={queryKey}
            isReadOnly={isReadOnly ?? false}
            setRowUploading={setRowUploading}
          />
        )}
        {!isXs && (
          <FilesDisplay
            isRowDropZoneEnable
            dropZoneUploaderProps={
              docType.allowUpload &&
              !isReadOnly &&
              transmittalType !== ETransmittalType.ICE &&
              transmittalType !== ETransmittalType.REVIEWER
                ? {
                    eventHandlers: {
                      addedfile: onAddedFile,
                      addedfiles: onAddedFiles,
                    },
                    acceptedFiles: TRANSMITTAL_ITEM_ACCEPTED_FILES,
                  }
                : undefined
            }
            header={docType.name}
            id={docType.id}
            files={formFileDrawingItems}
            listViewColumns={columns}
            listViewProps={{
              responsive: false,
              classNames: {
                row: ((item: object, cExtras: TableCellExtras, rExtras: TableRowExtras) => {
                  return (
                    rExtras.rowState === 'Editing' ? 'gcl-gems-transmittal-document-table-row-editing' : ''
                  ) as string;
                }) as GetClassNameByItemFn,
              },
              cellExtras: {
                defaultFileType,
                transmittalType,
                transmittalStatus,
                isAnyActionLoading,
                setIsAnyActionLoading,
                setRowUploading,
                isRefetchingTransmittalItems,
                isSelectAllTransmittalRevisionsForApproval,
                onSelectAllTransmittalRevisionsForApproval,
                mutationSelectAllTransmittalRevisionsForApproval,
                mutationUpdateAllToLatestRevisionST,
                updateAllToLatestRevisionST,
                hasDeletePermission,
                useFormMethods,
                editingRowCount,
                formFileDrawingItems,
                isAllLatestRevisionST,
                isSubmitEnabled,
                isWindowFocus,
                filesToUpload,
                setFilesToUpload,
              },
            }}
            rowExtras={{
              getDropZoneUploaderProps: ({ row, rowIndex }) => {
                if (
                  !docType.allowUpload ||
                  isReadOnly ||
                  transmittalType === ETransmittalType.ICE ||
                  transmittalType === ETransmittalType.REVIEWER ||
                  transmittalType !== formFileDrawingItems[rowIndex].transmittalItem?.initiatedProjectTransmittalType
                )
                  return undefined;
                return {
                  customMessage: `Drop to ${formFileDrawingItems[rowIndex].form?.item?.docNo}`,
                  eventHandlers: {
                    addedfile: (file: any) => {
                      onAddedFile(file, true, rowIndex);
                    },
                  },
                };
              },
            }}
            onRenderExtraHeaderButtons={(defaultButtons) => extraHeaderButtons}
          />
        )}
      </ContentLoader>
    </div>
  );
};

const createFormTextFieldCell = (
  useFormMethods: UseFormReturn,

  tablePrefix: string,
  id: string,
  getValue: (item: IFormFileDrawingItem) => string | undefined,
  generateIdleDisplayElememt = (item: IFormFileDrawingItem, value: string | undefined) => <div>{value}</div>,
) => {
  return createTableCell<string, any>(
    (tempValue: string, item: IFormFileDrawingItem, index: number, cellExtras: { [key: string]: any }, rowExtras) => {
      return renderFormTextFieldCell(
        useFormMethods,
        tempValue,
        item,
        index,
        cellExtras,
        rowExtras,
        `${tablePrefix}[${index}].${ETransmittalItemTableFormKey.form}.${id}`,
        getValue,
        generateIdleDisplayElememt,
      );
    },
  );
};

export const renderFormTextFieldCell = (
  useFormMethods: UseFormReturn,
  tempValue: string,
  item: IFormFileDrawingItem,
  index: number,
  cellExtras: { [key: string]: any },
  rowExtras: TableRowExtras,
  id: string,
  getValue: (item: IFormFileDrawingItem) => string | undefined,
  generateIdleDisplayElement = (fileItem: IFormFileDrawingItem, value: string | undefined) => <div>{value}</div>,
) => {
  const { rowState } = rowExtras;
  const value = useMemo(() => {
    return getValue(item);
  }, [JSON.stringify(item)]);

  if (!useFormMethods) return null;
  const { handleSubmit, ...restFormMethods } = useFormMethods;

  return rowState === 'Idle' ? (
    generateIdleDisplayElement(item, value)
  ) : (
    <FormTextField
      id={id}
      formFieldMethods={restFormMethods}
      value={value}
      disabled={rowState === 'Loading' || rowState === 'Disabled'}
    />
  );
};
